/**
 * Whether the window at the other end of a link is still there, from the
 * Web Locks API instead of heartbeats. Each window holds a lock named after
 * its end for as long as it lives; the browser releases it when the window
 * closes, reloads or crashes, however that happens. No timers are involved,
 * so a hidden window - whose timers the browser throttles to once a minute -
 * is never taken for a gone one.
 */

/** The part of `navigator.locks` presence needs. */
export type Locks = {
	request: (
		name: string,
		options: { signal?: AbortSignal },
		callback: (lock: unknown) => Promise<unknown> | unknown
	) => Promise<unknown>
}

export type LinkEnd = 'host' | 'replica'

export const presenceLockName = (link: string, end: LinkEnd): string =>
	`beelday-replication:${link}:${end}`

export type Presence = { stop: () => void }

const ignoreAbort = (): void => undefined

/**
 * Holds this window's lock until `stop` or the window goes. `onHeld` runs
 * once it is held: a window speaks only then, or the other end could look
 * for the lock before there is one. Without Web Locks it runs at once.
 */
export const holdPresence = (
	locks: Locks | undefined,
	name: string,
	onHeld: () => void
): Presence => {
	if (!locks) {
		onHeld()
		return { stop: () => undefined }
	}
	const controller = new AbortController()
	let release = (): void => undefined
	locks
		.request(name, { signal: controller.signal }, () => {
			onHeld()
			return new Promise<void>(resolve => {
				release = resolve
			})
		})
		.catch(ignoreAbort)
	return {
		stop: () => {
			controller.abort()
			release()
		},
	}
}

/**
 * Calls `onGone` once the holder of `name` lets it go - at once if nobody
 * holds it, so arm it only after the other end has spoken. The lock is let go
 * of again the moment it is granted. Without Web Locks it never calls.
 */
export const watchPresence = (
	locks: Locks | undefined,
	name: string,
	onGone: () => void
): Presence => {
	if (!locks) {
		return { stop: () => undefined }
	}
	const controller = new AbortController()
	locks
		.request(name, { signal: controller.signal }, () => {
			if (!controller.signal.aborted) {
				onGone()
			}
		})
		.catch(ignoreAbort)
	return { stop: () => controller.abort() }
}

export type PeerTracker = {
	/** The other end said something: it is there. */
	heard: () => void
	stop: () => void
}

/**
 * The other end's presence: there from the first message it sends, gone when
 * its lock is let go of, there again with its next message (a reload).
 */
export const trackPeer = (
	locks: Locks | undefined,
	name: string,
	onPeer: (present: boolean) => void
): PeerTracker => {
	let watch: Presence | undefined
	return {
		heard: () => {
			if (watch) {
				return
			}
			watch = watchPresence(locks, name, () => {
				watch = undefined
				onPeer(false)
			})
			onPeer(true)
		},
		stop: () => {
			watch?.stop()
			watch = undefined
		},
	}
}
